/**
 * Style Section Registry — maps (gameType, layoutStyle, sectionType)
 * to style-specific React components for GridEngine rendering.
 *
 * When GridEngine renders a section, it checks here first.
 * If a style-specific renderer exists, it's used instead of the
 * generic sectionRegistry default. Shared sections (bonus_draws,
 * compact_winners, leaderboard, etc.) are NOT registered here —
 * they use sectionRegistry defaults.
 */

// Classic 50/50 — Standard
import { StandardHeroSection, StandardLiveDrawSection, StandardLastDrawInfoSection, StandardHowItWorksSection } from './standard'

// Classic 50/50 — V2
import { V2HeroSection, V2LiveDrawSection } from './v2'

// Classic 50/50 — Premium
import {
  PremiumHeroSection, PremiumStatsWinnersSection,
  PremiumCharityImpactSection, PremiumFinalCTASection,
} from './premium'

// Classic 50/50 — PremiumPlus
import {
  PremiumPlusHeroSection, PremiumPlusTopPartnersSection,
} from './premiumPlus'

// Classic 50/50 — Style2
import { Style2HeroSection } from './style2'

// Classic 50/50 — Style4 / Style5 (Theater)
import { TheaterHeroSection } from './style4'

// Classic 50/50 — Style1 (Modern Grid) / Fluid — hero_carousel + shared LiveDrawCard
import { Style1HeroCarouselSection, SharedLiveDrawSection } from './style1'

// Classic 50/50 — Style3 (Feature Focus) — hero_carousel + charity_banner
import { Style3HeroCarouselSection, Style3CharityBannerSection } from './style3'

// Classic 50/50 — Style5 (Spotlight) — live_draw, how_it_works, final_cta
import { Style5LiveDrawSection, Style5HowItWorksSection, Style5FinalCTASection } from './style5'

// Progressive 50/50 — Standard
import {
  ProgressiveStandardHeroSection,
  ProgressiveStandardLiveDrawSection,
} from './progressive/standard'

// Progressive 50/50 — Style2
import {
  ProgressiveStyle2HeroSection,
  ProgressiveStyle2QuickStatsSection,
  ProgressiveStyle2HowItWorksSection,
} from './progressive/style2'

// Progressive 50/50 — Style3
import {
  ProgressiveStyle3HeroSection,
  ProgressiveStyle3JackpotSummarySection,
  ProgressiveStyle3FinalCTASection,
} from './progressive/style3'

// ── Chase the Ace ─────────────────────────────────────────────────
import { ChaseStandardHeroSection, ChaseStandardHowItWorksSection } from './chaseTheAce/standard'
import { ChaseStyle1HeroSection, ChaseStyle1CardsRemainingSection, ChaseStyle1HowItWorksSection, ChaseStyle1FinalCTASection } from './chaseTheAce/style1'
import { ChaseV2HeroSection } from './chaseTheAce/v2'
import { ChaseDeckRunnerHeroSection, ChaseDeckRunnerJackpotSummarySection, ChaseDeckRunnerStatsWinnersSection, ChaseDeckRunnerHowItWorksSection, ChaseDeckRunnerFinalCTASection } from './chaseTheAce/deckRunner'
import { ChaseStorybookHeroSection } from './chaseTheAce/storybook'

// ── Elimination Draw ──────────────────────────────────────────────
import { EliminationStandardHeroSection, EliminationStandardLiveDrawSection } from './elimination/standard'
import { EliminationStyle1HeroSection, EliminationStyle1HowItWorksSection, EliminationStyle1FinalCTASection } from './elimination/style1'
import { EliminationStyle2HeroSection, EliminationStyle2HowItWorksSection } from './elimination/style2'

// ── Prize Draw ────────────────────────────────────────────────────
import { PrizeDrawStandardHeroSection, PrizeDrawStandardLiveDrawSection, PrizeDrawStandardHowItWorksSection } from './prizeDraw/standard'
import { PrizeDrawStyle1HeroSection, PrizeDrawStyle1HowItWorksSection } from './prizeDraw/style1'
import { PrizeDrawGalleryHeroSection, PrizeDrawGalleryJackpotSummarySection, PrizeDrawGalleryFinalCTASection } from './prizeDraw/gallerySpotlight'

// ── Square Game ───────────────────────────────────────────────────
import { SquareStandardHeroSection, SquareStandardLiveDrawSection, SquareStandardHowItWorksSection } from './squareGame/standard'
import { SquareCharityHeroSection, SquareCharityHeroSpotlight, SquareCharityLiveDrawSection, SquareCharityHowItWorksSection } from './squareGame/charity'
import { SquareScoreboardHeroSection } from './squareGame/scoreboard'
import SquareGridSection from '../../components/sections/SquareGridSection'

// ── Superball Bingo ───────────────────────────────────────────────
import { BingoStandardHeroSection, BingoStandardHowItWorksSection } from './superballBingo/standard'
import { BingoStyle1HeroSection, BingoStyle1HowItWorksSection, BingoStyle1FinalCTASection } from './superballBingo/style1'
import { BingoStyle2HeroSection } from './superballBingo/style2'
import { BingoStyle3HeroSection } from './superballBingo/style3'
import { BingoLiveBoardHeroSection, BingoLiveBoardJackpotSummarySection, BingoLiveBoardStatsWinnersSection, BingoLiveBoardHowItWorksSection, BingoLiveBoardFinalCTASection } from './superballBingo/liveBoard'


/**
 * Registry keyed by gameType → layoutStyle → sectionType → Component.
 * Only style-specific sections are registered. Shared sections use
 * sectionRegistry defaults automatically.
 */
const STYLE_SECTIONS = {
  classic5050: {
    standard: {
      hero_section: StandardHeroSection,
      live_draw: StandardLiveDrawSection,
      last_draw_info: StandardLastDrawInfoSection,
      how_it_works: StandardHowItWorksSection,
    },
    'classic-v2': {
      hero_section: V2HeroSection,
      live_draw: V2LiveDrawSection,
    },
    premium: {
      hero_carousel: PremiumHeroSection,
      hero_section: PremiumHeroSection,
      stats_winners: PremiumStatsWinnersSection,
      charity_impact: PremiumCharityImpactSection,
      final_cta: PremiumFinalCTASection,
    },
    'premium-plus': {
      hero_carousel: PremiumPlusHeroSection,
      hero_section: PremiumPlusHeroSection,
      // PremiumPlus shares Premium's stats_winners, charity_impact, final_cta
      stats_winners: PremiumStatsWinnersSection,
      charity_impact: PremiumCharityImpactSection,
      final_cta: PremiumFinalCTASection,
      top_partners: PremiumPlusTopPartnersSection,
    },
    style1: {
      hero_carousel: Style1HeroCarouselSection,
      live_draw: SharedLiveDrawSection,
    },
    style2: {
      hero_section: Style2HeroSection,
      live_draw: SharedLiveDrawSection,
    },
    style3: {
      hero_carousel: Style3HeroCarouselSection,
      live_draw: SharedLiveDrawSection,
      charity_banner: Style3CharityBannerSection,
    },
    style4: {
      hero_section: TheaterHeroSection,
      live_draw: SharedLiveDrawSection,
    },
    style5: {
      hero_section: TheaterHeroSection,
      hero_carousel: TheaterHeroSection,
      live_draw: Style5LiveDrawSection,
      how_it_works: Style5HowItWorksSection,
      final_cta: Style5FinalCTASection,
    },
    fluid: {
      hero_carousel: Style1HeroCarouselSection,
      live_draw: SharedLiveDrawSection,
    },
  },
  // Progressive 50/50
  progressive5050: {
    standard: {
      hero_section: ProgressiveStandardHeroSection,
      live_draw: ProgressiveStandardLiveDrawSection,
    },
    style2: {
      hero_section: ProgressiveStyle2HeroSection,
      quick_stats: ProgressiveStyle2QuickStatsSection,
      how_it_works: ProgressiveStyle2HowItWorksSection,
    },
    style3: {
      hero_section: ProgressiveStyle3HeroSection,
      jackpot_summary: ProgressiveStyle3JackpotSummarySection,
      final_cta: ProgressiveStyle3FinalCTASection,
    },
  },
  // Modified 50/50 uses the same style sections as Classic 50/50
  modified5050: {
    standard: {
      hero_section: StandardHeroSection,
      live_draw: StandardLiveDrawSection,
      last_draw_info: StandardLastDrawInfoSection,
      how_it_works: StandardHowItWorksSection,
    },
    'classic-v2': {
      hero_section: V2HeroSection,
      live_draw: V2LiveDrawSection,
    },
    'modified-v2': {
      hero_section: V2HeroSection,
      live_draw: V2LiveDrawSection,
    },
    premium: {
      hero_carousel: PremiumHeroSection,
      hero_section: PremiumHeroSection,
      stats_winners: PremiumStatsWinnersSection,
      charity_impact: PremiumCharityImpactSection,
      final_cta: PremiumFinalCTASection,
    },
    'premium-plus': {
      hero_carousel: PremiumPlusHeroSection,
      hero_section: PremiumPlusHeroSection,
      stats_winners: PremiumStatsWinnersSection,
      charity_impact: PremiumCharityImpactSection,
      final_cta: PremiumFinalCTASection,
      top_partners: PremiumPlusTopPartnersSection,
    },
    style1: {
      hero_carousel: Style1HeroCarouselSection,
      live_draw: SharedLiveDrawSection,
    },
    style2: {
      hero_section: Style2HeroSection,
      live_draw: SharedLiveDrawSection,
    },
    style3: {
      hero_carousel: Style3HeroCarouselSection,
      live_draw: SharedLiveDrawSection,
      charity_banner: Style3CharityBannerSection,
    },
    style4: {
      hero_section: TheaterHeroSection,
      live_draw: SharedLiveDrawSection,
    },
    style5: {
      hero_section: TheaterHeroSection,
      hero_carousel: TheaterHeroSection,
      live_draw: Style5LiveDrawSection,
      how_it_works: Style5HowItWorksSection,
      final_cta: Style5FinalCTASection,
    },
    fluid: {
      hero_carousel: Style1HeroCarouselSection,
      live_draw: SharedLiveDrawSection,
    },
  },

  // ── Chase the Ace ────────────────────────────────────────────
  chaseTheAce: {
    standard: {
      hero_section: ChaseStandardHeroSection,
      how_it_works: ChaseStandardHowItWorksSection,
    },
    style1: {
      hero_section: ChaseStyle1HeroSection,
      cards_remaining: ChaseStyle1CardsRemainingSection,
      how_it_works: ChaseStyle1HowItWorksSection,
      final_cta: ChaseStyle1FinalCTASection,
    },
    'chase-v2': {
      hero_section: ChaseV2HeroSection,
    },
    deckRunner: {
      hero_section: ChaseDeckRunnerHeroSection,
      jackpot_summary: ChaseDeckRunnerJackpotSummarySection,
      stats_winners: ChaseDeckRunnerStatsWinnersSection,
      how_it_works: ChaseDeckRunnerHowItWorksSection,
      final_cta: ChaseDeckRunnerFinalCTASection,
    },
    premium: {
      hero_section: ChaseStorybookHeroSection,
      stats_winners: PremiumStatsWinnersSection,
      charity_impact: PremiumCharityImpactSection,
      final_cta: PremiumFinalCTASection,
    },
    storybook: {
      hero_section: ChaseStorybookHeroSection,
    },
  },

  // ── Elimination Draw ─────────────────────────────────────────
  eliminationDraw: {
    standard: {
      hero_section: EliminationStandardHeroSection,
      live_draw: EliminationStandardLiveDrawSection,
    },
    style1: {
      hero_section: EliminationStyle1HeroSection,
      how_it_works: EliminationStyle1HowItWorksSection,
      final_cta: EliminationStyle1FinalCTASection,
    },
    style2: {
      hero_section: EliminationStyle2HeroSection,
      how_it_works: EliminationStyle2HowItWorksSection,
    },
  },

  // ── Prize Draw ───────────────────────────────────────────────
  prizeDraw: {
    standard: {
      hero_section: PrizeDrawStandardHeroSection,
      live_draw: PrizeDrawStandardLiveDrawSection,
      how_it_works: PrizeDrawStandardHowItWorksSection,
    },
    style1: {
      hero_section: PrizeDrawStyle1HeroSection,
      how_it_works: PrizeDrawStyle1HowItWorksSection,
    },
    gallerySpotlight: {
      hero_section: PrizeDrawGalleryHeroSection,
      jackpot_summary: PrizeDrawGalleryJackpotSummarySection,
      final_cta: PrizeDrawGalleryFinalCTASection,
    },
  },

  // ── Square Game ──────────────────────────────────────────────
  squareGame: {
    standard: {
      hero_section: SquareStandardHeroSection,
      live_draw: SquareStandardLiveDrawSection,
      how_it_works: SquareStandardHowItWorksSection,
      square_grid: SquareGridSection,
    },
    charity: {
      hero_section: SquareCharityHeroSection,
      charity_hero: SquareCharityHeroSpotlight,
      live_draw: SquareCharityLiveDrawSection,
      how_it_works: SquareCharityHowItWorksSection,
      square_grid: SquareGridSection,
    },
    scoreboard: {
      hero_section: SquareScoreboardHeroSection,
      square_grid: SquareGridSection,
    },
  },

  // ── Superball Bingo ──────────────────────────────────────────
  superballBingo: {
    standard: {
      hero_section: BingoStandardHeroSection,
      how_it_works: BingoStandardHowItWorksSection,
    },
    style1: {
      hero_section: BingoStyle1HeroSection,
      how_it_works: BingoStyle1HowItWorksSection,
      final_cta: BingoStyle1FinalCTASection,
    },
    style2: {
      hero_section: BingoStyle2HeroSection,
    },
    style3: {
      hero_section: BingoStyle3HeroSection,
    },
    liveBoard: {
      hero_section: BingoLiveBoardHeroSection,
      jackpot_summary: BingoLiveBoardJackpotSummarySection,
      stats_winners: BingoLiveBoardStatsWinnersSection,
      how_it_works: BingoLiveBoardHowItWorksSection,
      final_cta: BingoLiveBoardFinalCTASection,
    },
  },
}

/**
 * Normalize style identifiers — handles '1' vs 'style1', 'v2' vs 'classic-v2', etc.
 * Game-type-aware: checks against the specific gameType's styles first.
 */
function normalizeStyle(style, gameType) {
  if (!style) return 'standard'
  const s = String(style).toLowerCase().trim()

  // Direct match against the specific game type's styles
  const gameStyles = gameType ? STYLE_SECTIONS[gameType] : null
  if (gameStyles?.[s]) return s

  // Also check classic5050 as a general fallback for shared styles
  if (STYLE_SECTIONS.classic5050?.[s]) return s

  // Numeric shorthand: '2' → 'style2', '4' → 'style4', etc.
  if (/^\d+$/.test(s)) return `style${s}`

  // 'v2' → 'classic-v2'
  if (s === 'v2') return 'classic-v2'

  return s
}

/**
 * Get a style-specific section renderer for the given game type, layout style, and section type.
 * Returns a React component or null if no style-specific renderer exists.
 *
 * @param {string} gameType - e.g. 'classic5050', 'modified5050'
 * @param {string} layoutStyle - e.g. 'standard', 'premium', 'style4'
 * @param {string} sectionType - e.g. 'hero_section', 'live_draw'
 * @returns {React.ComponentType|null}
 */
export function getStyleSectionRenderer(gameType, layoutStyle, sectionType) {
  const normalizedStyle = normalizeStyle(layoutStyle, gameType)
  const gameStyles = STYLE_SECTIONS[gameType]
  if (!gameStyles) return null

  const styleEntry = gameStyles[normalizedStyle]
  if (!styleEntry) return null

  return styleEntry[sectionType] || null
}

/**
 * Check if a section type has a style-specific renderer for the given game + style.
 */
export function hasStyleSection(gameType, layoutStyle, sectionType) {
  return getStyleSectionRenderer(gameType, layoutStyle, sectionType) !== null
}

/**
 * Get all style-specific section types for a game + style combination.
 * Useful for debugging and admin tools.
 */
export function getStyleSectionTypes(gameType, layoutStyle) {
  const normalizedStyle = normalizeStyle(layoutStyle, gameType)
  const gameStyles = STYLE_SECTIONS[gameType]
  if (!gameStyles) return []

  const styleEntry = gameStyles[normalizedStyle]
  if (!styleEntry) return []

  return Object.keys(styleEntry)
}
